/**
 * Microphone input-level metering.
 *
 * @module lib/utils/audio-level
 */

/**
 * Reference RMS that maps to a full meter. Raw mic input (autoGainControl off)
 * is fairly quiet, so a low full-scale reference keeps normal speech visibly
 * filling the bar without pinning, while loud input still clamps to 100.
 */
const RMS_FULL_SCALE = 0.15;

/**
 * How much of the previous level to keep while the signal is falling. Higher =
 * slower decay. Only applied on release (see {@link smoothMicLevel}).
 */
const RELEASE_FACTOR = 0.85;

/**
 * Convert a frame of time-domain audio samples into a 0-100 input level for the
 * mic meter.
 *
 * Uses RMS (root-mean-square) amplitude of the waveform, which tracks how loud
 * the input actually is. Averaging frequency-domain bins instead (the previous
 * approach) over-reads badly: with a small FFT the few wide low-frequency bins
 * pick up room hum/DC and saturate, pinning the bar near full even in silence.
 *
 * @param timeDomainData Byte samples from `AnalyserNode.getByteTimeDomainData`,
 *   where 128 is the zero crossing (silence) and 0/255 are the extremes.
 * @returns Input level clamped to the inclusive range [0, 100].
 */
export function computeMicLevel(timeDomainData: Uint8Array): number {
  if (timeDomainData.length === 0) return 0;

  let sumSquares = 0;
  for (let i = 0; i < timeDomainData.length; i++) {
    const sample = (timeDomainData[i] - 128) / 128; // -1..1, 0 = silence
    sumSquares += sample * sample;
  }
  const rms = Math.sqrt(sumSquares / timeDomainData.length); // 0..1

  return Math.min(100, Math.max(0, (rms / RMS_FULL_SCALE) * 100));
}

/**
 * Smooth a fast-changing instantaneous level into a steady meter reading.
 *
 * Uses VU-meter dynamics: instant attack so the bar jumps the moment sound
 * arrives, and a slow release so it eases back down instead of flickering
 * frame-to-frame (the per-frame RMS is jittery, which otherwise makes both the
 * bar and any threshold-based label twitch).
 *
 * @param previous The last displayed level, in [0, 100].
 * @param next The new instantaneous level from {@link computeMicLevel}.
 * @returns The level to display next.
 */
export function smoothMicLevel(previous: number, next: number): number {
  if (next >= previous) return next; // attack: rise immediately
  return previous * RELEASE_FACTOR + next * (1 - RELEASE_FACTOR); // release: ease down
}
